// Persistent bottom-right tray showing every tracked background job:
// processing (live progress bar), ready (View action), failed (error +
// dismiss). Mounted in DashboardLayout so it is visible from any page —
// the user who kicks off a contract upload and navigates away still sees
// progress, and a page reload re-hydrates it from sessionStorage.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCard, SpecraIconButton, SpecraInlineBanner, SpecraProgress, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProcessingJobsStore } from '../../lib/processing/jobsStore';
// How long a completed contract-doc job stays visible before auto-removal.
// Long enough for the user to click View from the toast; short enough that
// back-navigation never shows a stale completed card.
const READY_AUTO_REMOVE_MS = 4000;
export function ProcessingTray() {
    const jobs = useProcessingJobsStore((s)=>s.jobs);
    const entries = Object.values(jobs).sort((a, b)=>b.startedAt - a.startedAt);
    if (entries.length === 0) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2",
        "data-testid": Testids.processing.tray,
        children: entries.map((job)=>/*#__PURE__*/ _jsx(TrayJob, {
                job: job
            }, job.batchId))
    });
}
function TrayJob({ job }) {
    const removeJob = useProcessingJobsStore((s)=>s.removeJob);
    // Auto-remove contract-doc jobs after a short delay once they reach 'ready'.
    // TrayJob is the right owner: it's mounted for all job statuses (the tray
    // reads all jobs, not just 'processing' ones), so the timer stays alive and
    // the cleanup cancels it if the user clicks View or Dismiss first.
    // Allowlist: only contract-docs opts in — new job kinds must explicitly add
    // themselves here rather than silently inheriting auto-removal.
    useEffect(()=>{
        if (job.status !== 'ready' || job.kind !== 'contract-docs') return;
        const timer = setTimeout(()=>removeJob(job.batchId), READY_AUTO_REMOVE_MS);
        return ()=>clearTimeout(timer);
    }, [
        job.status,
        job.kind,
        job.batchId,
        removeJob
    ]);
    return /*#__PURE__*/ _jsx(SpecraCard, {
        className: "p-3 shadow-lg",
        "data-testid": Testids.processing.trayJob,
        "data-job-id": job.batchId,
        "data-status": job.status,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-start gap-2",
            children: [
                job.status === 'processing' && /*#__PURE__*/ _jsx(Loader2, {
                    className: "mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-muted-foreground"
                }),
                job.status === 'ready' && /*#__PURE__*/ _jsx(CheckCircle2, {
                    className: "mt-0.5 h-4 w-4 flex-shrink-0 text-green-600"
                }),
                job.status === 'failed' && /*#__PURE__*/ _jsx(XCircle, {
                    className: "mt-0.5 h-4 w-4 flex-shrink-0 text-destructive"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "min-w-0 flex-1 space-y-1.5",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "truncate text-sm font-medium leading-tight",
                            children: job.label
                        }),
                        job.status === 'processing' && /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraProgress, {
                                    value: job.percent,
                                    "aria-label": "Processing progress",
                                    className: "h-1.5 flex-1",
                                    "data-testid": Testids.processing.trayJobProgress,
                                    "data-percent": job.percent
                                }),
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "text-xs tabular-nums text-muted-foreground",
                                    children: [
                                        job.percent,
                                        "%"
                                    ]
                                })
                            ]
                        }),
                        job.status === 'ready' && job.link && /*#__PURE__*/ _jsx(Link, {
                            to: job.link,
                            className: "text-xs font-medium text-primary hover:underline",
                            "data-testid": Testids.processing.trayJobLink,
                            onClick: ()=>{
                                // Contract-doc jobs are terminal once viewed. Award-intake
                                // jobs ARE the Launch panel's state (ready + the award doc
                                // id Begin Award Comparison needs) — consuming one here
                                // would land View on the upload form, looking exactly like
                                // the cancellation this tray exists to disprove. The panel
                                // removes it when the comparison begins; the completion
                                // toast's View never removes either.
                                if (job.kind !== 'award-intake') removeJob(job.batchId);
                            },
                            children: "View"
                        }),
                        job.status === 'failed' && job.notAContract && /*#__PURE__*/ _jsx(NotAContractAlert, {
                            reason: job.notAContract,
                            uploadHref: job.link ?? `/project/${job.zouProjectId}`
                        }),
                        job.status === 'failed' && !job.notAContract && /*#__PURE__*/ _jsx("p", {
                            className: "text-xs text-destructive",
                            "data-testid": Testids.processing.trayJobError,
                            children: job.error ?? 'Processing failed'
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraIconButton, {
                    variant: "ghost",
                    label: "Dismiss",
                    className: "h-5 w-5 flex-shrink-0 text-muted-foreground",
                    onClick: ()=>removeJob(job.batchId),
                    "data-testid": Testids.processing.trayJobDismiss,
                    children: /*#__PURE__*/ _jsx(X, {
                        className: "h-3.5 w-3.5"
                    })
                })
            ]
        })
    });
}
/** What to tell the user when an upload was refused as not a signed contract. */ export function notAContractCopy(reason) {
    const body = 'Upload the signed award or modification.';
    switch(reason){
        case 'solicitation':
            return {
                title: "This doesn't look like a signed contract",
                body: `It looks like a solicitation (RFQ or RFP). ${body}`
            };
        case 'unconfirmed':
            return {
                title: "We couldn't confirm this is a signed contract",
                body
            };
        default:
            return {
                title: "This doesn't look like a signed contract",
                body
            };
    }
}
/**
 * Blocking alert for an upload refused as not a signed contract. Nothing was
 * built from it; the action opens the page holding the upload control.
 */ export function NotAContractAlert({ reason, uploadHref }) {
    const copy = notAContractCopy(reason);
    return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "danger",
        variant: "framed",
        title: copy.title,
        className: "px-3",
        "data-testid": Testids.processing.notAContractAlert,
        "data-reason": reason,
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: copy.body
            }),
            /*#__PURE__*/ _jsx(Link, {
                to: uploadHref,
                className: `${specraButtonVariants({
                    variant: 'secondary',
                    size: 'sm'
                })} mt-2`,
                "data-testid": Testids.processing.notAContractUpload,
                children: "Upload the signed award"
            })
        ]
    });
}
